import { createContext, useContext, useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const justLoggedInRef = useRef(false);

  useEffect(() => {
    // Returning from Google OAuth: GoogleAuthCallback exchanges the session_id first — skip /auth/me.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const checkAuth = async () => {
      // Set a timeout to prevent infinite loading
      const timeoutId = setTimeout(() => {
        console.warn("Auth check timeout - proceeding without authentication");
        setLoading(false);
      }, 5000);

      if (token) {
        // Skip re-check if login just happened (user already set)
        if (justLoggedInRef.current) {
          justLoggedInRef.current = false;
          clearTimeout(timeoutId);
          setLoading(false);
          return;
        }
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 4000 // 4 second timeout for the request
          });
          setUser(response.data);
        } catch (error) {
          console.warn("Auth check failed:", error.message);
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      clearTimeout(timeoutId);
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;

    // Mark authentication time to prevent service worker reload
    window.lastAuthTime = Date.now();

    // Skip redundant /auth/me check since we already have user data
    justLoggedInRef.current = true;

    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);

    // Auto-sync calendars for God Admin on login (priority sync - next 2 weeks)
    if (userData.role === "god_admin") {
      try {
        // Non-blocking priority sync
        axios.post(`${API}/calendar/ical-sync-priority`, {}, {
          headers: { Authorization: `Bearer ${access_token}` }
        }).catch(err => console.log("Calendar sync skipped:", err.message));
      } catch (e) {
        // Ignore sync errors on login
      }
    }

    return userData;
  };

  // Google sign-in: backend already exchanged the session and issued our JWT
  const loginWithToken = (access_token, userData) => {
    window.lastAuthTime = Date.now();
    justLoggedInRef.current = true;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(newUser);
    return newUser;
  };

  // Re-fetch the current user (e.g. after completing a profile)
  const refreshUser = async () => {
    const t = localStorage.getItem("token");
    if (!t) return null;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setUser(response.data);
      return response.data;
    } catch (e) {
      return null;
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  // Session timeout: 30 min for workers/clients, 60 min for admins
  useEffect(() => {
    if (!user) return;
    const isAdmin = user.role === "god_admin" || user.role === "admin";
    const timeoutMs = isAdmin ? 60 * 60 * 1000 : 30 * 60 * 1000;
    let timer;

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        logout();
        window.location.href = "/login?expired=1";
      }, timeoutMs);
    };

    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();

    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Global scroll unlock - prevents Radix UI from permanently locking scroll on mobile
  useEffect(() => {
    const unlockScroll = () => {
      const body = document.body;
      if (body.hasAttribute('data-scroll-locked') || body.style.overflow === 'hidden') {
        body.style.pointerEvents = '';
        body.style.overflow = '';
        body.removeAttribute('data-scroll-locked');
      }
    };
    // Check periodically for stuck scroll locks
    const interval = setInterval(unlockScroll, 2000);
    // Also check on route changes / visibility
    document.addEventListener('visibilitychange', unlockScroll);
    window.addEventListener('popstate', unlockScroll);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', unlockScroll);
      window.removeEventListener('popstate', unlockScroll);
    };
  }, []);

  const value = {
    user,
    token,
    loading,
    login,
    loginWithToken,
    register,
    refreshUser,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === "god_admin" || user?.role === "admin",
    isGodAdmin: user?.role === "god_admin",
    isLeadWorker: user?.role === "lead_worker",
    isWorker: user?.role === "worker" || user?.role === "lead_worker",
    isClient: user?.role === "client"
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
